// The account page (`account/AccountPage.tsx`, `/account`): who is signed in,
// the connected accounts, sign-out. The connection rows themselves speak with
// the `focus.github.*` / `focus.figma.*` keys their components own; only the
// row titles live here, and the Gmail and API key rows' own copy. The brand
// titles read the same in both languages.

import type { MsgSection } from "./types.js";

export const account: MsgSection = {
  // The quiet top-bar link on the Focus picker and main menu.
  "account.navLabel": { en: "Account", de: "Konto" },
  "account.headline": { en: "Your account", de: "Dein Konto" },
  "account.signedInAs": {
    en: "Signed in as {name} (@{username}).",
    de: "Angemeldet als {name} (@{username}).",
  },
  "account.signedInAsUsername": {
    en: "Signed in as @{username}.",
    de: "Angemeldet als @{username}.",
  },
  "account.connections": { en: "Connections", de: "Verbindungen" },
  "account.github.title": { en: "GitHub", de: "GitHub" },
  "account.figma.title": { en: "Figma", de: "Figma" },
  "account.google.title": { en: "Google", de: "Google" },
  // The Gmail row (`account/GmailConnection.tsx`, the Gmail add-on): which
  // Google account the add-on in Gmail belongs to.
  "account.gmail.title": { en: "Gmail add-on", de: "Gmail-Add-on" },
  "account.gmail.notLinked": { en: "Not connected.", de: "Nicht verbunden." },
  "account.gmail.linked": { en: "Gmail is connected.", de: "Gmail ist verbunden." },
  "account.gmail.link": { en: "Connect Gmail", de: "Gmail verbinden" },
  "account.gmail.unlink": { en: "Disconnect Gmail", de: "Gmail trennen" },
  "account.gmail.unlinkShort": { en: "Disconnect", de: "Trennen" },
  "account.gmail.unlinked": { en: "Gmail is disconnected.", de: "Gmail ist getrennt." },
  "account.gmail.unlinkFailed": {
    en: "Loom couldn't disconnect Gmail. Try again.",
    de: "Loom konnte Gmail nicht trennen. Versuch es nochmals.",
  },
  "account.gmail.result.domainRefused": {
    en: "Loom connects only panter.ch Google accounts. Pick your Panter account.",
    de: "Loom verbindet nur Google-Konten von panter.ch. Wähl dein Panter-Konto.",
  },
  "account.gmail.result.linkedElsewhere": {
    en: "This Google account already belongs to another Loom account.",
    de: "Dieses Google-Konto gehört schon zu einem anderen Loom-Konto.",
  },
  "account.gmail.result.error": {
    en: "The connection didn't finish. Try again.",
    de: "Die Verbindung kam nicht zustande. Versuch es nochmals.",
  },
  // The API key row (`account/AnthropicKeyConnection.tsx`), read only. The
  // field, Checking… and the key check's lines are the admin page's
  // (`admin/AnthropicKeysAdminPage.tsx`).
  "account.anthropic.title": { en: "Anthropic API key", de: "Anthropic-API-Schlüssel" },
  "account.anthropic.none": { en: "No key assigned.", de: "Kein Schlüssel zugewiesen." },
  "account.anthropic.assigned": {
    en: "An admin assigned your key.",
    de: "Ein Admin hat deinen Schlüssel zugewiesen.",
  },
  "account.anthropic.spend": {
    en: "Last 30 days on this key: {total}",
    de: "Letzte 30 Tage auf diesem Schlüssel: {total}",
  },
  "account.anthropic.spendSplit": {
    en: "Last 30 days on this key: {total} ({split})",
    de: "Letzte 30 Tage auf diesem Schlüssel: {total} ({split})",
  },
  "account.anthropic.field": { en: "API key", de: "API-Schlüssel" },
  "account.anthropic.checking": { en: "Checking…", de: "Wird geprüft…" },
  "account.anthropic.error.malformed": {
    en: "That isn't an Anthropic API key.",
    de: "Das ist kein Anthropic-API-Schlüssel.",
  },
  "account.anthropic.error.rejected": {
    en: "Anthropic refused this key.",
    de: "Anthropic hat diesen Schlüssel abgelehnt.",
  },
  "account.anthropic.error.checkFailed": {
    en: "Loom couldn't check the key with Anthropic. Try again.",
    de: "Loom konnte den Schlüssel bei Anthropic nicht prüfen. Versuch es nochmals.",
  },
  "account.loadFailed": {
    en: "Loom couldn't load all your connections. Reload the page.",
    de: "Loom konnte nicht alle Verbindungen laden. Lade die Seite neu.",
  },
  // After a GitHub connect: the new projects are listed in the Focus picker.
  "account.toProjects": { en: "Go to your projects", de: "Zu deinen Projekten" },
  "account.signOut": { en: "Sign out", de: "Abmelden" },
  // Under the Focus picker, only while a connection Loom can no longer renew
  // keeps projects out of the list. It does not name which one; the account
  // page does.
  "account.hint.lapsed": {
    en: "A connection has run out, so some projects are missing here.",
    de: "Eine Verbindung ist abgelaufen, darum fehlen hier Projekte.",
  },
  "account.hint.open": { en: "Open account", de: "Konto öffnen" },
};
